'use client';

import { Box } from '@terra/ui-styled-system';
import {
  ChatHarProvider,
  TraceFilesProvider,
  type TraceExpandedSections,
} from '@terra/ui-design-system';
import { useMemo, useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAppTargetId } from '../../store/projectStore';
import { useAgentExecutionHistory } from '../../hooks/api/useAgentExecutionHistory';
import { useSmokeTestHistory } from '../../hooks/api/useSmokeTestHistory';
import { useAgentExecutionSSE } from '../../hooks/useAgentExecutionSSE';
import { useBoundedQueryRefresh } from '../../hooks/useBoundedQueryRefresh';
import { adaptAgentExecutionHistory } from '../../utils/adaptAgentExecutionHistory';
import { AgentExecutionTraceContent } from './AgentExecutionTraceContent';
import { mergeAgentExecutionSmokeHistory } from './merge-agent-execution-smoke-history';
import { agentExecutionClient } from '@terra/shared-rest-clients-agent-execution';
import { terraProxyApiClient } from '../../clients/testerClient';
import { useHarById } from '../../hooks/api/useHarById';
import { useHarDisplayConfig } from '../../hooks/useHarDisplayConfig';
import { useToolHarPresence } from '../../hooks/useToolHarPresence';
import { TraceViewSettingsStoreProvider } from '../../providers/TraceViewSettingsStoreProvider';
import { useTabViewStateStore } from '../../store/tabViewStateStore';
import {
  useLayoutStore,
  type AgentExecutionMainContentTab,
} from '../../store/layoutStore';

type AgentExecutionTraceTabProps = {
  tab: AgentExecutionMainContentTab;
};

export function AgentExecutionTraceTab({ tab }: AgentExecutionTraceTabProps) {
  const appTargetId = useAppTargetId();
  const updateTab = useLayoutStore((state) => state.updateMainContentTab);

  const executionId = tab.data.executionId;

  const savedViewState = useTabViewStateStore((state) =>
    executionId ? state.traceStates[executionId] : undefined,
  );
  const setTraceViewState = useTabViewStateStore(
    (state) => state.setTraceViewState,
  );

  const handleExpandedSectionsChange = useCallback(
    (newSections: TraceExpandedSections) => {
      if (executionId) {
        setTraceViewState(executionId, { expandedSections: newSections });
      }
    },
    [executionId, setTraceViewState],
  );

  const handleExecutionTabChange = useCallback(
    (nextTab: string) => {
      if (executionId) {
        setTraceViewState(executionId, { executionSubTab: nextTab });
      }
    },
    [executionId, setTraceViewState],
  );

  const {
    data: historyResponse,
    isLoading: isLoadingHistory,
    error,
    refetch,
    invalidate,
  } = useAgentExecutionHistory({
    executionId,
    enabled: !!executionId,
  });

  const refreshHistory = useBoundedQueryRefresh({
    refetch,
  });

  const handleAbortSuccess = useCallback(() => {
    return refreshHistory({
      shouldScheduleFollowUp: (history) => history?.status !== 'ABORTED',
    });
  }, [refreshHistory]);

  const handleResumeSuccess = useCallback(() => {
    return refreshHistory({
      shouldScheduleFollowUp: (history) => history?.status !== 'RUNNING',
    });
  }, [refreshHistory]);

  const handleDeleteSuccess = useCallback(() => {
    return invalidate();
  }, [invalidate]);

  const { data: traces, isLoading: isLoadingTraces } = useQuery({
    queryKey: ['agent-execution-traces', executionId],
    queryFn: async () => {
      const response = await agentExecutionClient.getTraces({
        params: { executionId },
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch execution traces');
      }

      return response.body;
    },
    enabled: !!executionId,
    staleTime: 30000,
  });

  const firstTraceThreadId = traces?.[0]?.threadId;

  const handleTraceEvent = useCallback(() => {
    refetch();
  }, [refetch]);

  useAgentExecutionSSE({
    executionId,
    enabled: !!executionId,
    onTraceEvent: handleTraceEvent,
  });

  const { data: smokeTestHistory } = useSmokeTestHistory({
    threadId: firstTraceThreadId || '',
    enabled: Boolean(appTargetId && firstTraceThreadId),
  });

  const isLoading = isLoadingHistory || isLoadingTraces;

  const adaptedHistory = useMemo(() => {
    if (!historyResponse) {
      return undefined;
    }

    const adapted = adaptAgentExecutionHistory(historyResponse);

    return mergeAgentExecutionSmokeHistory({
      adapted,
      smokeTestHistory,
    });
  }, [historyResponse, smokeTestHistory]);

  const fetchHarData = useCallback(
    async ({
      threadId,
      toolCallId,
    }: {
      threadId: string;
      toolCallId: string;
    }) => {
      try {
        const response = await terraProxyApiClient.getToolHarsByToolCallId({
          threadId,
          toolCallId,
        });

        if (response && response.hars) {
          return response.hars;
        }

        console.error('Failed to fetch HAR data:', response);

        return [];
      } catch (fetchError) {
        console.error('Error fetching HAR data:', fetchError);

        return [];
      }
    },
    [],
  );

  const { fetchHarById } = useHarById();
  const { hasHarPresence } = useToolHarPresence(firstTraceThreadId);
  const harDisplay = useHarDisplayConfig(appTargetId);

  const traceFiles = useMemo(() => {
    return (
      adaptedHistory?.files?.filter(
        (file): file is NonNullable<typeof file> => file !== null,
      ) || []
    );
  }, [adaptedHistory]);

  const executionTitle = historyResponse?.summary ?? '';

  useEffect(() => {
    if (historyResponse && tab.id) {
      const title = executionTitle
        ? `Execution: ${executionTitle}`
        : 'Execution';

      updateTab(tab.id, { title });
    }
  }, [executionTitle, tab.id, updateTab, historyResponse]);

  return (
    <Box h="full" w="full">
      <TraceViewSettingsStoreProvider>
        <ChatHarProvider
          threadId={firstTraceThreadId ?? null}
          fetchHarData={fetchHarData}
          fetchHarById={fetchHarById}
          hasHarPresence={hasHarPresence}
          harDisplay={harDisplay}
        >
          <TraceFilesProvider traceFiles={traceFiles}>
            <AgentExecutionTraceContent
              applicationId={appTargetId ?? ''}
              executionId={executionId}
              threadId={firstTraceThreadId}
              title={executionTitle}
              history={adaptedHistory}
              isLoading={isLoading}
              error={error}
              expandedSections={savedViewState?.expandedSections}
              onExpandedSectionsChange={handleExpandedSectionsChange}
              executionTab={savedViewState?.executionSubTab ?? 'overview'}
              onExecutionTabChange={handleExecutionTabChange}
              onAbortSuccess={handleAbortSuccess}
              onResumeSuccess={handleResumeSuccess}
              onDeleteSuccess={handleDeleteSuccess}
            />
          </TraceFilesProvider>
        </ChatHarProvider>
      </TraceViewSettingsStoreProvider>
    </Box>
  );
}
